import React, { useState, useMemo, useRef, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { uploadBlogContentImage } from "../../services/api";

const SERVER_URL = process.env.REACT_APP_SERVER_URL || "";

const slugify = (str) =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export const EMPTY_BLOG_FORM = {
  title: "",
  slug: "",
  seoTitle: "",
  metaDescription: "",
  keywords: "",
  category: "General",
  headImageAlt: "",
  content: "",
  customSchema: "",
  status: "published",
};

const styles = `
  .blogform-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  @media (max-width: 800px) { .blogform-grid { grid-template-columns: 1fr; } }
  .blogform-label {
    display: block; font-size: 13px; font-weight: 600;
    color: #374151; margin: 0 0 6px;
  }
  .blogform-hint { font-size: 11.5px; color: #9ca3af; margin-top: 4px; }
  .blogform-input, .blogform-textarea, .blogform-select {
    width: 100%; padding: 9px 12px; border: 1px solid #d9dbe8;
    border-radius: 8px; font-size: 14px; font-family: inherit;
    background: #fff; box-sizing: border-box;
  }
  .blogform-input:focus, .blogform-textarea:focus { outline: none; border-color: #6246ea; }
  .blogform-textarea { resize: vertical; min-height: 70px; }
  .blogform-section {
    font-family: "Syne", sans-serif; font-weight: 700; font-size: 15px;
    color: #6246ea; margin: 26px 0 14px; padding-top: 18px;
    border-top: 1px dashed #e5e7f2;
  }
  .blogform-field { margin-bottom: 14px; }
  .blogform-charcount { font-size: 11px; color: #9ca3af; text-align: right; }
  .blogform-charcount.over { color: #ef4444; font-weight: 600; }
  .blogform-imgpreview {
    max-width: 320px; max-height: 180px; border-radius: 10px;
    display: block; margin-top: 10px; object-fit: cover;
  }
  .blogform-faq-row {
    border: 1px solid #e8eaf2; border-radius: 10px;
    padding: 14px; margin-bottom: 12px; background: #fafbff;
  }
  .blogform-faq-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
  .blogform-faq-num { font-size: 12px; font-weight: 700; color: #6246ea; }
  .blogform-faq-remove {
    background: none; border: none; color: #ef4444; cursor: pointer;
    font-size: 12.5px; font-weight: 600; padding: 2px 6px;
  }
  .blogform-add-btn {
    background: #6246ea14; color: #6246ea; border: 1px dashed #6246ea66;
    border-radius: 8px; padding: 9px 16px; font-size: 13.5px;
    font-weight: 600; cursor: pointer;
  }
  .blogform-editor .ql-container { min-height: 320px; font-size: 15px; }
  .blogform-editor .ql-toolbar, .blogform-editor .ql-container {
    border-color: #d9dbe8; background: #fff;
  }
  .blogform-editor .ql-toolbar { border-radius: 8px 8px 0 0; }
  .blogform-editor .ql-container { border-radius: 0 0 8px 8px; }
`;

// Form state is owned by the parent (add/edit page); this renders all fields.
const BlogForm = ({
  form,
  setForm,
  faqs,
  setFaqs,
  headImageFile,
  setHeadImageFile,
  existingHeadImage,
  toast,
}) => {
  const quillRef = useRef(null);
  const [slugTouched, setSlugTouched] = useState(!!form.slug);
  const [previewUrl, setPreviewUrl] = useState(null);

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "title" && !slugTouched) next.slug = slugify(value);
      if (field === "slug") next.slug = value;
      return next;
    });
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    setForm((prev) => ({ ...prev, slug: e.target.value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast("Image must be under 8MB", "error");
      return;
    }
    setHeadImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  // Quill image button → upload to server → insert returned URL
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("image", file);
      try {
        const res = await uploadBlogContentImage(fd);
        const url = `${SERVER_URL}${res.data.url}`;
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", url);
          quill.setSelection(range.index + 1);
        }
      } catch (err) {
        toast(
          err.response?.data?.message || "Image upload failed",
          "error",
        );
      }
    };
  }, [toast]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["blockquote", "link", "image"],
          ["clean"],
        ],
        handlers: { image: imageHandler },
      },
    }),
    [imageHandler],
  );

  const setFaq = (i, key, value) =>
    setFaqs((prev) => prev.map((f, idx) => (idx === i ? { ...f, [key]: value } : f)));

  const metaLen = form.metaDescription.length;

  return (
    <>
      <style>{styles}</style>

      <div className="blogform-section" style={{ borderTop: "none", paddingTop: 0, marginTop: 0 }}>
        📄 Article
      </div>

      <div className="blogform-field">
        <label className="blogform-label">Title (H1) *</label>
        <input
          className="blogform-input"
          placeholder="e.g. ROC Compliance Course Fees & Duration Explained (2026 Guide)"
          value={form.title}
          onChange={set("title")}
        />
      </div>

      <div className="blogform-grid">
        <div className="blogform-field">
          <label className="blogform-label">URL Slug *</label>
          <input
            className="blogform-input"
            placeholder="roc-compliance-course-fees-duration"
            value={form.slug}
            onChange={handleSlugChange}
            onBlur={(e) =>
              setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }))
            }
          />
          <div className="blogform-hint">skillyer.com/blog/{form.slug || "…"}</div>
        </div>
        <div className="blogform-field">
          <label className="blogform-label">Category</label>
          <input
            className="blogform-input"
            placeholder="e.g. Business"
            value={form.category}
            onChange={set("category")}
          />
        </div>
      </div>

      <div className="blogform-section">🔍 SEO</div>

      <div className="blogform-field">
        <label className="blogform-label">SEO Title Tag *</label>
        <input
          className="blogform-input"
          placeholder="e.g. ROC Compliance Course Fees & Duration 2026 | Complete Guide"
          value={form.seoTitle}
          onChange={set("seoTitle")}
        />
        <div className={`blogform-charcount${form.seoTitle.length > 60 ? " over" : ""}`}>
          {form.seoTitle.length}/60
        </div>
      </div>

      <div className="blogform-field">
        <label className="blogform-label">Meta Description *</label>
        <textarea
          className="blogform-textarea"
          placeholder="~155 characters shown in Google results"
          value={form.metaDescription}
          onChange={set("metaDescription")}
        />
        <div className={`blogform-charcount${metaLen > 160 ? " over" : ""}`}>
          {metaLen}/160
        </div>
      </div>

      <div className="blogform-field">
        <label className="blogform-label">Keywords</label>
        <textarea
          className="blogform-textarea"
          placeholder="primary, secondary and LSI keywords, comma-separated"
          value={form.keywords}
          onChange={set("keywords")}
        />
        <div className="blogform-hint">
          Primary + secondary + LSI terms, comma-separated (used in the keywords meta tag)
        </div>
      </div>

      <div className="blogform-section">🖼️ Head Image</div>

      <div className="blogform-grid">
        <div className="blogform-field">
          <label className="blogform-label">Image (also used as og:image)</label>
          <input
            className="blogform-input"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          {(previewUrl || existingHeadImage) && (
            <img
              className="blogform-imgpreview"
              src={previewUrl || `${SERVER_URL}${existingHeadImage}`}
              alt="Head preview"
            />
          )}
        </div>
        <div className="blogform-field">
          <label className="blogform-label">Image Alt Text</label>
          <input
            className="blogform-input"
            placeholder="Describes the image for SEO & accessibility"
            value={form.headImageAlt}
            onChange={set("headImageAlt")}
          />
        </div>
      </div>

      <div className="blogform-section">✍️ Content</div>

      <div className="blogform-field blogform-editor">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={form.content}
          onChange={(html) => setForm((prev) => ({ ...prev, content: html }))}
          modules={modules}
          placeholder="Write the article… Use the image button in the toolbar to upload images into the content."
        />
      </div>

      <div className="blogform-section">❓ FAQs (generates FAQPage schema)</div>

      {faqs.map((f, i) => (
        <div className="blogform-faq-row" key={i}>
          <div className="blogform-faq-head">
            <span className="blogform-faq-num">Q{i + 1}</span>
            <button
              type="button"
              className="blogform-faq-remove"
              onClick={() => setFaqs((prev) => prev.filter((_, idx) => idx !== i))}
            >
              ✕ Remove
            </button>
          </div>
          <div className="blogform-field" style={{ marginBottom: 8 }}>
            <input
              className="blogform-input"
              placeholder="Question"
              value={f.q}
              onChange={(e) => setFaq(i, "q", e.target.value)}
            />
          </div>
          <textarea
            className="blogform-textarea"
            placeholder="Answer"
            value={f.a}
            onChange={(e) => setFaq(i, "a", e.target.value)}
          />
        </div>
      ))}
      <button
        type="button"
        className="blogform-add-btn"
        onClick={() => setFaqs((prev) => [...prev, { q: "", a: "" }])}
      >
        + Add FAQ
      </button>

      <div className="blogform-section">⚙️ Advanced</div>

      <div className="blogform-field">
        <label className="blogform-label">Custom Schema (JSON-LD, optional)</label>
        <textarea
          className="blogform-textarea"
          style={{ fontFamily: "monospace", fontSize: 12.5, minHeight: 110 }}
          placeholder='e.g. {"@context":"https://schema.org","@type":"Course", ...}'
          value={form.customSchema}
          onChange={set("customSchema")}
        />
        <div className="blogform-hint">
          Extra structured data (e.g. Course schema). BlogPosting, FAQPage and
          BreadcrumbList schema are added automatically.
        </div>
      </div>

      <div className="blogform-field" style={{ maxWidth: 240 }}>
        <label className="blogform-label">Status</label>
        <select className="blogform-select" value={form.status} onChange={set("status")}>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>
    </>
  );
};

export const buildBlogFormData = (form, faqs, headImageFile) => {
  const fd = new FormData();
  Object.entries(form).forEach(([k, v]) => fd.append(k, v));
  fd.append(
    "faqs",
    JSON.stringify(faqs.filter((f) => f.q.trim() && f.a.trim())),
  );
  if (headImageFile) fd.append("headImage", headImageFile);
  return fd;
};

export const validateBlogForm = (form) => {
  if (!form.title.trim()) return "Title is required";
  if (!form.slug.trim()) return "Slug is required";
  if (!form.seoTitle.trim()) return "SEO title tag is required";
  if (!form.metaDescription.trim()) return "Meta description is required";
  if (!form.content || form.content === "<p><br></p>")
    return "Content is required";
  if (form.customSchema.trim()) {
    try {
      JSON.parse(form.customSchema);
    } catch {
      return "Custom schema is not valid JSON";
    }
  }
  return null;
};

export default BlogForm;
